import { useEffect, useState } from "react";
import { FileText, Sparkles, Upload, BarChart3 } from "lucide-react";

import { Button, Card, ScrollArea } from "@/components/ui";
import { CONTRACT_TEMPLATES } from "@/mockData/contractTemplates";
import { PARSE_STEPS, RECENT_CONTRACTS, SAMPLE_CONTRACT } from "@/mockData/contractIntelligence";
import { TemplateIcon } from "@/components/contract-intelligence/templates/TemplateIcon";
import type { ContractIntelligenceContract } from "@/types/contractIntelligence.types";
import { cn } from "@/lib/utils";
import { TextShimmer } from "@/components/primitives/text-shimmer";

interface ContractIntelligenceUploadProps {
  onLoad: (contract: ContractIntelligenceContract) => void;
  onGenerate: () => void;
  onCompare: () => void;
}

function ParseProgress() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setStep((s) => Math.min(s + 1, PARSE_STEPS.length)), 280);
    return () => window.clearInterval(id);
  }, []);

  const pct = Math.min(Math.round((step / PARSE_STEPS.length) * 100), 100);

  return (
    <Card className="p-8">
      <div className="mb-4 flex items-center gap-2">
        <div className="size-4 animate-spin rounded-full border-2 border-muted border-t-primary" />
        <span className="text-sm font-semibold">Analyzing contract…</span>
        <span className="ml-auto font-mono text-xs text-muted-foreground">{pct}%</span>
      </div>
      <ul className="space-y-2">
        {PARSE_STEPS.map((label, idx) => (
          <li
            key={label}
            className={cn(
              "flex items-center gap-2 text-sm transition-colors",
              idx < step ? "text-foreground" : "text-muted-foreground",
            )}
          >
            <span className="inline-flex size-3.5 items-center justify-center">
              {idx < step ? (
                <span className="size-2 rounded-full bg-primary" />
              ) : idx === step ? (
                <span className="size-2 animate-pulse rounded-full bg-primary" />
              ) : (
                <span className="size-1.5 rounded-full bg-muted" />
              )}
            </span>
            {label}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function ContractIntelligenceUpload({
  onLoad,
  onGenerate,
  onCompare,
}: ContractIntelligenceUploadProps) {
  const [drag, setDrag] = useState(false);
  const [parsing, setParsing] = useState(false);

  const startParse = () => {
    setParsing(true);
    window.setTimeout(() => {
      setParsing(false);
      onLoad(SAMPLE_CONTRACT);
    }, 1700);
  };

  return (
    <div className="flex w-full h-full min-h-0 overflow-hidden">
      <div className="w-full min-w-0 min-h-0 flex-1 overflow-scroll">
        <div className="mx-auto flex max-w-3xl flex-col items-center justify-center px-6 py-10">
          <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            Contract Intelligence
          </p>
          <h1 className="text-center text-3xl font-semibold tracking-tight">
            Drop a contract. Get the deal in 60 seconds.
          </h1>
          <p className="mt-2 max-w-xl text-center text-sm leading-relaxed text-muted-foreground">
            Upload any agreement and Ari extracts financials, key dates, ownership splits, and risks — then
            redline in plain English.
          </p>

          {!parsing && (
            <div className="mt-6 grid w-full gap-2.5 sm:grid-cols-3">
              <button
                type="button"
                onClick={onGenerate}
                className="flex min-h-[138px] flex-col items-start gap-2 rounded-2xl border p-3.5 text-left bg-card"
              >
                <span className="flex items-center gap-1.5 text-xs font-semibold">
                  <Sparkles className="size-3.5" aria-hidden="true" />
                  Generate
                </span>
                <span className="text-sm font-semibold">Start a new contract with Ari</span>
                <span className="text-xs text-muted-foreground">Pick a template, answer a few questions.</span>
              </button>
              <button
                type="button"
                onClick={startParse}
                className="flex min-h-[138px] flex-col items-start gap-2 rounded-2xl border p-3.5 text-left bg-card"
              >
                <span className="flex items-center gap-1.5 text-xs font-semibold">
                  <Upload className="size-3.5" aria-hidden="true" />
                  Upload
                </span>
                <span className="text-sm font-semibold">Analyze a draft you have</span>
                <span className="text-xs text-muted-foreground">PDF or DOCX — Ari extracts terms and flags risks.</span>
              </button>
              <button
                type="button"
                onClick={onCompare}
                className="relative flex min-h-[138px] flex-col items-start gap-2 rounded-2xl border p-3.5 text-left bg-card"
              >
                <div className="absolute top-2.5 right-2.5 rounded-full bg-blue-500/10 px-2 py-1 flex items-center justify-center">
                  <TextShimmer spread={1.5} repeatDelay={3} duration={1.5} className="text-xs font-semibold dark:[--base-color:var(--color-blue-400)] dark:[--base-gradient-color:#fff]">
                    3 offers
                  </TextShimmer>
                </div>
                <span className="flex items-center gap-1.5 text-xs font-semibold">
                  <BarChart3 className="size-3.5" aria-hidden="true" />
                  Compare
                </span>
                <span className="text-sm font-semibold">Weigh competing offers</span>
                <span className="text-xs text-muted-foreground">Side-by-side deal comparison.</span>
              </button>
            </div>
          )}

          <div className="mt-4 w-full">
            {parsing ? (
              <ParseProgress />
            ) : (
              <button
                type="button"
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  startParse();
                }}
                onClick={startParse}
                className={cn(
                  "w-full rounded-xl border border-dashed p-10 text-center transition-colors",
                  drag ? "border-primary bg-primary/5" : "border-border bg-card/40 hover:bg-muted/30",
                )}
              >
                <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-indigo-200 text-white shadow-lg shadow-primary/30">
                  <Upload className="size-7" aria-hidden="true" />
                </div>
                <p className="text-sm font-semibold">Drop a contract or click to browse</p>
                <p className="mt-1 text-xs text-muted-foreground">PDF, DOCX, or pasted text · Up to 200 pages</p>
              </button>
            )}
          </div>

          <div className="mt-3 flex w-full flex-wrap items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={startParse}>
              Try sample: Recording Deal — Alexandra Rivers
            </Button>
            <p className="text-xs text-muted-foreground">
              Or pull from Vault · Email · DocuSign
            </p>
          </div>

          <div className="mt-8 grid w-full gap-2 sm:grid-cols-3">
            {[
              { title: "Extract terms", sub: "Money, dates, rights, obligations", icon: FileText },
              { title: "Flag risks", sub: "Off-market clauses, ownership, IP", icon: Sparkles },
              { title: "Edit in chat", sub: '"Push back on §5.1 master rights"', icon: Sparkles },
            ].map((item) => (
              <Card key={item.title} className="gap-1 p-3.5">
                <item.icon className="mb-2 size-4 text-foreground/70" aria-hidden="true" />
                <p className="text-xs font-semibold">{item.title}</p>
                <p className="text-[11px] text-muted-foreground">{item.sub}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <aside className="flex w-[300px] shrink-0 flex-col overflow-hidden border-l border-border">
        <ScrollArea className="min-h-0 flex-1 p-4">
          <p className="mb-3 text-xs font-semibold text-muted-foreground">
            Recently analyzed
          </p>
          <div className="space-y-1.5">
            {RECENT_CONTRACTS.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={startParse}
                className="w-full rounded-2xl bg-card p-2.5 text-left transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      c.risk === "high" && "bg-destructive",
                      c.risk === "medium" && "bg-amber-500",
                      c.risk === "low" && "bg-success",
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate text-xs font-medium">{c.name}</span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[10.5px] text-muted-foreground">
                  <span className="rounded-full px-2 py-0.5 font-medium" style={{ backgroundColor: `${c.color}22`, color: c.color }}>
                    {c.type}
                  </span>
                  <span>{c.date}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-muted-foreground">
              Template library
            </p>
            <button
              type="button"
              onClick={onGenerate}
              className="text-xs font-medium text-foreground hover:underline"
            >
              View all
            </button>
          </div>
          <div className="mt-2 space-y-1.5">
            {CONTRACT_TEMPLATES.slice(0, 4).map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={onGenerate}
                className="flex w-full items-center gap-2.5 rounded-2xl bg-card p-2.5 text-left"
              >
                <span
                  className="flex size-8 shrink-0 items-center justify-center rounded-md text-white"
                  style={{ background: `linear-gradient(135deg, ${template.color}, ${template.color}cc)` }}
                >
                  <TemplateIcon name={template.icon} className="size-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium">{template.name}</span>
                  <span className="text-[10px] text-muted-foreground">{template.pages} pages</span>
                </span>
              </button>
            ))}
          </div>
        </ScrollArea>
      </aside>
    </div>
  );
}
